/* ═══════════════════════════════════════════════════════════════
   The Designer — table helpers (Phase 1 item 3, 2026-08-25).

   Thin chrome over prosemirror-tables' OWN commands. Nothing here
   re-implements table structure: rows, columns, merges, header rows
   and cell navigation are all the framework's (`addRowAfter`,
   `addColumnAfter`, `mergeCells`, `toggleHeaderRow`, `goToNextCell`,
   `columnResizing`). What this file adds is only:

     · "is the selection in a table" / "which cell", answered with the
       framework's own `isInTable` / `selectedRect`
     · put the selection IN a given cell before running a framework
       command (what a hover affordance and a right-click need)
     · read/merge our own per-cell `borders` attr — an attr the
       framework has no opinion about, so `setCellAttribute` (which
       writes one flat value) cannot merge it
     · write the TABLE-level attrs the framework has no opinion about
       either (width, cell padding, column shares, justification) — each
       in ONE transaction, so each is ONE Ctrl+Z whether it was fired
       from a toolbar button or the right-click menu
     · split one table into two at the selected row — the only structural
       edit in this file, because prosemirror-tables ships no split-table
       command of its own

   NOT here, deliberately: a "repeat header row" switch. Word has ONE
   flag for that (`w:tblHeader`) and it lives on the header row itself,
   so the framework's `toggleHeaderRow` already IS the repeat toggle —
   a second control would only be a second thing to get out of sync.
   ═══════════════════════════════════════════════════════════════ */

import type { Editor } from '@tiptap/react';
import { Fragment } from '@tiptap/pm/model';
import type { Node as PMNode } from '@tiptap/pm/model';
import { TextSelection } from '@tiptap/pm/state';
import type { EditorState, Transaction } from '@tiptap/pm/state';
import { CellSelection, isInTable, selectedRect } from '@tiptap/pm/tables';
import type {
  BorderLine,
  CellBorderValue,
  CellSide,
  TableCell as TableCellData,
  TableCellBorders,
  TableEdge,
} from '../../shared/types';
import { isBorderLine, TABLE_EDGES } from '../../shared/types';
import {
  type BorderTarget,
  CELL_BORDER_SIDES,
  evenColPct,
  MAX_TABLE_WIDTH_PCT,
  MIN_TABLE_WIDTH_PCT,
  normalizeCellBorders,
  TABLE_NODE_NAME,
  type TableStyleAttrs,
} from './tables';
import { newBlockId } from './nodeBase';

/** Is the selection inside a table? The framework's own predicate. */
export function inTable(state: EditorState): boolean {
  return isInTable(state);
}

/** Every cell the current selection covers — one caret in a cell gives that
 *  one cell, a CellSelection gives the whole block. Positions are absolute
 *  document positions, deduplicated (a merged cell appears once). */
function selectedCells(state: EditorState): Array<{ pos: number; attrs: Record<string, unknown> }> {
  if (!isInTable(state)) return [];
  const rect = selectedRect(state);
  const seen = new Set<number>();
  const out: Array<{ pos: number; attrs: Record<string, unknown> }> = [];
  for (let row = rect.top; row < rect.bottom; row++) {
    for (let col = rect.left; col < rect.right; col++) {
      const cellPos = rect.map.map[row * rect.map.width + col];
      if (seen.has(cellPos)) continue;
      seen.add(cellPos);
      const node = rect.table.nodeAt(cellPos);
      if (node) out.push({ pos: rect.tableStart + cellPos, attrs: node.attrs });
    }
  }
  return out;
}

/** The border overrides of the first cell in the selection — what the UI
 *  shows as "current". */
export function selectedCellBorders(state: EditorState): TableCellBorders | null {
  const cells = selectedCells(state);
  if (!cells.length) return null;
  return normalizeCellBorders(cells[0].attrs.borders as TableCellBorders | null);
}

/** The shading of the first cell in the selection, or null for "no fill". */
export function selectedCellShade(state: EditorState): string | null {
  const cells = selectedCells(state);
  if (!cells.length) return null;
  return (cells[0].attrs.bgColor as string | null) || null;
}

/** Rewrite the `borders` attr of every selected cell. `update` receives that
 *  cell's current overrides and returns the new ones (null = back to the
 *  table's own grid). */
export function updateCellBorders(
  editor: Editor,
  update: (current: TableCellBorders | null) => TableCellBorders | null
): boolean {
  const { state } = editor;
  const cells = selectedCells(state);
  if (!cells.length) return false;
  let tr: Transaction | null = null;
  for (const cell of cells) {
    const next = normalizeCellBorders(
      update(normalizeCellBorders(cell.attrs.borders as TableCellBorders | null))
    );
    tr = (tr || state.tr).setNodeAttribute(cell.pos, 'borders', next);
  }
  if (!tr) return false;
  editor.view.dispatch(tr);
  return true;
}

// ─── Word-style borders (TABLE_BORDERS_AND_RENAME_PLAN T1.2) ───

/** Is there a real multi-cell (or dragged single-cell) selection? Word's
 *  rule: with one, the Borders controls hit the selected cells; without one
 *  (a caret in a table) they hit the whole table's own lines. */
export function hasCellSelection(state: EditorState): boolean {
  return state.selection instanceof CellSelection;
}

type TableRect = ReturnType<typeof selectedRect>;
interface SideWrite {
  /** Cell position relative to the table's content start (TableMap's unit). */
  cellPos: number;
  side: CellSide;
  value: CellBorderValue;
}

const OPPOSITE: Record<CellSide, CellSide> = {
  top: 'bottom',
  bottom: 'top',
  left: 'right',
  right: 'left',
};

/** Unique cell positions (TableMap units) inside the rectangle. */
function rectCells(rect: TableRect): number[] {
  const seen = new Set<number>();
  for (let row = rect.top; row < rect.bottom; row++) {
    for (let col = rect.left; col < rect.right; col++) {
      seen.add(rect.map.map[row * rect.map.width + col]);
    }
  }
  return [...seen];
}

/** The cells across one side of a cell — resolved through TableMap, so a
 *  merged neighbour (or a merged cell with several neighbours) is found
 *  correctly. Empty for a side on the table's own outer edge. */
function neighboursAcross(rect: TableRect, cellPos: number, side: CellSide): number[] {
  const { map } = rect;
  const r = map.findCell(cellPos);
  const out = new Set<number>();
  if (side === 'top' && r.top > 0)
    for (let c = r.left; c < r.right; c++) out.add(map.map[(r.top - 1) * map.width + c]);
  if (side === 'bottom' && r.bottom < map.height)
    for (let c = r.left; c < r.right; c++) out.add(map.map[r.bottom * map.width + c]);
  if (side === 'left' && r.left > 0)
    for (let row = r.top; row < r.bottom; row++) out.add(map.map[row * map.width + r.left - 1]);
  if (side === 'right' && r.right < map.width)
    for (let row = r.top; row < r.bottom; row++) out.add(map.map[row * map.width + r.right]);
  out.delete(cellPos);
  return [...out];
}

/** Write cell sides in ONE transaction (one Ctrl+Z), applying the SHARED-EDGE
 *  rule: every side written also writes the neighbouring cell's opposite side
 *  to the same value, selected or not. Under `border-collapse` the heavier of
 *  two touching lines wins, so without this "No Border" would visibly remove
 *  nothing and a thin line could never replace a thick one. Word does the
 *  same. A side on the table's outer edge has no neighbour. */
function writeCellSides(editor: Editor, rect: TableRect, writes: SideWrite[]): boolean {
  if (!writes.length) return false;
  const patch = new Map<number, TableCellBorders>();
  const put = (pos: number, side: CellSide, value: CellBorderValue) => {
    const p = patch.get(pos) || {};
    p[side] = value;
    patch.set(pos, p);
  };
  // Neighbours first, then the explicit writes — so an explicit write always
  // has the last word on its own side.
  for (const w of writes)
    for (const n of neighboursAcross(rect, w.cellPos, w.side)) put(n, OPPOSITE[w.side], w.value);
  for (const w of writes) put(w.cellPos, w.side, w.value);
  const { state } = editor;
  const tr = state.tr;
  for (const [pos, p] of patch) {
    const node = rect.table.nodeAt(pos);
    if (!node) continue;
    const current = normalizeCellBorders(node.attrs.borders as TableCellBorders | null) || {};
    tr.setNodeAttribute(
      rect.tableStart + pos,
      'borders',
      normalizeCellBorders({ ...current, ...p })
    );
  }
  editor.view.dispatch(tr);
  return true;
}

/** What gets stored for a pen: a copy of the line, or `false` for the None
 *  pen (no line is no line, whichever way it was asked for). */
function cellValueFor(line: BorderLine): CellBorderValue {
  return line.style === 'none' || line.width <= 0
    ? false
    : { style: line.style, width: line.width };
}

/** Which sides of which cells a target hits inside the selection rectangle.
 *  A cell side is OUTER when it lies on the rectangle's own edge. */
function targetWrites(rect: TableRect, target: BorderTarget, value: CellBorderValue): SideWrite[] {
  const out: SideWrite[] = [];
  for (const cellPos of rectCells(rect)) {
    const r = rect.map.findCell(cellPos);
    const outer: Record<CellSide, boolean> = {
      top: r.top <= rect.top,
      bottom: r.bottom >= rect.bottom,
      left: r.left <= rect.left,
      right: r.right >= rect.right,
    };
    for (const side of CELL_BORDER_SIDES) {
      const horizontal = side === 'top' || side === 'bottom';
      const hit =
        target === 'all' || target === 'none'
          ? true
          : target === 'outside'
            ? outer[side]
            : target === 'inside'
              ? !outer[side]
              : target === 'insideH'
                ? horizontal && !outer[side]
                : target === 'insideV'
                  ? !horizontal && !outer[side]
                  : side === target && outer[side];
      if (hit) out.push({ cellPos, side, value: target === 'none' ? false : value });
    }
  }
  return out;
}

/** The table's own edges a target hits when there is no cell selection. */
const TABLE_TARGET_EDGES: Record<BorderTarget, readonly TableEdge[]> = {
  top: ['top'],
  bottom: ['bottom'],
  left: ['left'],
  right: ['right'],
  insideH: ['insideH'],
  insideV: ['insideV'],
  all: TABLE_EDGES,
  outside: ['top', 'bottom', 'left', 'right'],
  inside: ['insideH', 'insideV'],
  none: TABLE_EDGES,
};

/** Stamp `line` (the pen) at `target` — Word's Borders menu. ONE undo step.
 *   · Cell selection → the selection's cells (Outside = the rectangle's outer
 *     sides; Inside = the sides between selected cells; All = every side;
 *     No Border = `false` on every side), with the shared-edge rule.
 *   · Caret in a table → the TABLE's own edges (All = six, Outside = four,
 *     Inside = insideH + insideV, No Border = six to none).
 *  No Border ignores the pen. */
export function applyBorder(editor: Editor, target: BorderTarget, line: BorderLine): boolean {
  const { state } = editor;
  if (!isInTable(state)) return false;
  if (hasCellSelection(state)) {
    const rect = selectedRect(state);
    return writeCellSides(editor, rect, targetWrites(rect, target, cellValueFor(line)));
  }
  const stamp: BorderLine =
    target === 'none' || line.style === 'none' || line.width <= 0
      ? { style: 'none', width: 0 }
      : { style: line.style, width: line.width };
  return patchTableStyle(editor, (style) => {
    const edges = { ...(style.edges || {}) };
    for (const edge of TABLE_TARGET_EDGES[target]) edges[edge] = { ...stamp };
    return { ...style, edges };
  });
}

/** Every side of every selected cell on (true) / off (false) / back to the
 *  table's grid (null). On/off ride `applyBorder`'s cell path (All / No
 *  Border, with the shared-edge rule); a caret counts as a one-cell
 *  selection here, as it always has. */
export function setAllCellBorders(editor: Editor, on: boolean | null): boolean {
  if (on === null) return updateCellBorders(editor, () => null);
  const { state } = editor;
  if (!isInTable(state)) return false;
  const rect = selectedRect(state);
  return writeCellSides(editor, rect, targetWrites(rect, on ? 'all' : 'none', on));
}

/** Does this side currently draw a line? An untouched side (inheriting the
 *  table's grid) reads as ON. */
function sideShowing(v: CellBorderValue | undefined): boolean {
  if (v === false) return false;
  if (isBorderLine(v)) return v.style !== 'none' && v.width > 0;
  return true;
}

/** Flip one side of every selected cell. An untouched side (inheriting the
 *  table's grid) reads as ON, so the first click turns it off — what the eye
 *  expects from a line that is currently visible. Rides the same cell-side
 *  writer as `applyBorder`, shared-edge rule included. */
export function toggleCellBorderSide(
  editor: Editor,
  side: (typeof CELL_BORDER_SIDES)[number]
): boolean {
  const { state } = editor;
  if (!isInTable(state)) return false;
  const rect = selectedRect(state);
  const writes: SideWrite[] = rectCells(rect).map((cellPos) => {
    const node = rect.table.nodeAt(cellPos);
    const current = normalizeCellBorders(node?.attrs.borders as TableCellBorders | null);
    return { cellPos, side, value: !sideShowing(current?.[side]) };
  });
  return writeCellSides(editor, rect, writes);
}

/** The tint (0-100) of the first selected cell's fill; 100 when untinted. */
export function selectedCellTint(state: EditorState): number {
  const cells = selectedCells(state);
  if (!cells.length) return 100;
  const v = cells[0].attrs.bgOpacity as number | null;
  return typeof v === 'number' ? v : 100;
}

/** Write fill attrs to the shading scope in ONE transaction (one undo step):
 *  the selected cells, or — a bare caret — every cell of the table, the same
 *  rule borders follow. */
function writeShade(
  editor: Editor,
  bgColor: string | null,
  bgOpacity: number | null,
  record = true
): boolean {
  const { state } = editor;
  if (!isInTable(state)) return false;
  const rect = selectedRect(state);
  const whole = !hasCellSelection(state);
  const rels = new Set<number>();
  const r0 = whole ? 0 : rect.top;
  const r1 = whole ? rect.map.height : rect.bottom;
  const c0 = whole ? 0 : rect.left;
  const c1 = whole ? rect.map.width : rect.right;
  for (let row = r0; row < r1; row++) {
    for (let col = c0; col < c1; col++) rels.add(rect.map.map[row * rect.map.width + col]);
  }
  const tr = state.tr;
  for (const rel of rels) {
    const node = rect.table.nodeAt(rel);
    if (!node) continue;
    tr.setNodeMarkup(rect.tableStart + rel, undefined, { ...node.attrs, bgColor, bgOpacity });
  }
  if (!record) tr.setMeta('addToHistory', false);
  editor.view.dispatch(tr);
  return true;
}

/** Cell shading (hex null = No fill). `tint` is the existing `bgOpacity`. */
export function setCellShade(
  editor: Editor,
  hex: string | null,
  tint = 100,
  record = true
): boolean {
  return writeShade(editor, hex, hex ? tint : null, record);
}

/** A live colour drag is ONE undo step: intermediate applies skip history
 *  (`setCellShade(..., false)`); on release `commitShadeDrag` puts the cells
 *  back to what they were, unrecorded, then writes the final fill as the one
 *  recorded step — so a single undo returns the original fills. */
export type ShadeSnapshot = Array<{ pos: number; bgColor: unknown; bgOpacity: unknown }>;

export function snapshotShade(editor: Editor): ShadeSnapshot {
  const { state } = editor;
  if (!isInTable(state)) return [];
  const rect = selectedRect(state);
  const out: ShadeSnapshot = [];
  rect.table.descendants((node, pos) => {
    if (node.type.spec.tableRole === 'cell' || node.type.spec.tableRole === 'header_cell') {
      out.push({
        pos: rect.tableStart + pos,
        bgColor: node.attrs.bgColor,
        bgOpacity: node.attrs.bgOpacity,
      });
    }
    return false;
  });
  return out;
}

export function commitShadeDrag(
  editor: Editor,
  snap: ShadeSnapshot,
  hex: string,
  tint: number
): boolean {
  const tr = editor.state.tr;
  for (const c of snap) {
    const node = tr.doc.nodeAt(c.pos);
    if (node)
      tr.setNodeMarkup(c.pos, undefined, {
        ...node.attrs,
        bgColor: c.bgColor,
        bgOpacity: c.bgOpacity,
      });
  }
  tr.setMeta('addToHistory', false);
  editor.view.dispatch(tr);
  return setCellShade(editor, hex, tint);
}

/** Change only the tint of cells that already have a fill. */
export function setCellTint(editor: Editor, tint: number): boolean {
  const hex = selectedCellShade(editor.state);
  return hex ? writeShade(editor, hex, tint) : false;
}

/** Put the caret inside the cell at `cellPos` (the position of the td/th
 *  node itself), so a framework command that acts on "the current cell"
 *  acts on THAT one. */
export function focusCellAt(editor: Editor, cellPos: number): boolean {
  const { state } = editor;
  const inside = Math.min(cellPos + 2, state.doc.content.size);
  const $inside = state.doc.resolve(inside);
  editor.view.dispatch(state.tr.setSelection(TextSelection.near($inside)));
  return true;
}

/** Right-click / hover targeting: move the selection to the cell under the
 *  pointer so the menu acts on the cell the user pointed at — Word's rule.
 *  A selection that ALREADY covers that point (a multi-cell drag) is left
 *  alone: collapsing it would destroy the very thing "Merge cells" needs. */
export function selectCellAtCoords(editor: Editor, clientX: number, clientY: number): boolean {
  const found = editor.view.posAtCoords({ left: clientX, top: clientY });
  if (!found) return false;
  const { state } = editor;
  const { from, to } = state.selection;
  if (from !== to && found.pos >= from && found.pos <= to) return true;
  const $pos = state.doc.resolve(Math.min(Math.max(found.pos, 0), state.doc.content.size));
  editor.view.dispatch(state.tr.setSelection(TextSelection.near($pos)));
  return true;
}

// ─── Table-level style: width, padding, column shares, justification ───

/** Padding past this stops being padding and starts being a page. */
export const MAX_CELL_PADDING_PT = 36;

/** The table node the selection sits in, with its own document position. */
function tableAt(state: EditorState): { pos: number; node: PMNode } | null {
  const { $from } = state.selection;
  for (let d = $from.depth; d >= 0; d--) {
    const node = $from.node(d);
    if (node.type.name === TABLE_NODE_NAME) return { pos: $from.before(d), node };
  }
  return null;
}

/** The `tableStyle` of the table the selection is in — what a menu or a
 *  toolbar shows as "current". Empty when the selection is not in a table. */
export function tableStyleAt(state: EditorState): TableStyleAttrs {
  const found = tableAt(state);
  return (found?.node.attrs.tableStyle as TableStyleAttrs | undefined) || {};
}

/** Rewrite the table's `tableStyle` in ONE transaction, so every command
 *  below is one Ctrl+Z. `clearCellPx` also drops the framework resizer's
 *  per-cell pixels — what "the document's percentages are the truth again"
 *  means (T1.1: one unit, never a mix). */
function patchTableStyle(
  editor: Editor,
  patch: (style: TableStyleAttrs) => TableStyleAttrs,
  clearCellPx = false
): boolean {
  const { state } = editor;
  const found = tableAt(state);
  if (!found) return false;
  const style = (found.node.attrs.tableStyle || {}) as TableStyleAttrs;
  const tr = state.tr.setNodeAttribute(found.pos, 'tableStyle', patch({ ...style }));
  if (clearCellPx) {
    found.node.descendants((child, childPos) => {
      if (child.type.name === 'tableRow') return true;
      if (child.attrs.colwidth) tr.setNodeAttribute(found.pos + 1 + childPos, 'colwidth', null);
      return false;
    });
  }
  editor.view.dispatch(tr.scrollIntoView());
  return true;
}

/** Where the table sits across the page. null = back to the default, left. */
export function setTableJustify(
  editor: Editor,
  justify: 'left' | 'center' | 'right' | null
): boolean {
  return patchTableStyle(editor, (style) => {
    const next = { ...style };
    if (justify === null) delete next.tableJustify;
    else next.tableJustify = justify;
    return next;
  });
}

/** The table's own width as a % of the text column, clamped to printable. */
export function setTableWidth(editor: Editor, pct: number): boolean {
  if (!Number.isFinite(pct)) return false;
  const clamped = Math.min(Math.max(Math.round(pct), MIN_TABLE_WIDTH_PCT), MAX_TABLE_WIDTH_PCT);
  return patchTableStyle(editor, (style) => {
    const next = { ...style, width: clamped };
    delete next.autofit;
    return next;
  });
}

/** Every column the same share of the table — Word's "Distribute columns". */
export function distributeColumns(editor: Editor): boolean {
  const { state } = editor;
  if (!isInTable(state)) return false;
  const cols = selectedRect(state).map.width;
  if (cols <= 0) return false;
  return patchTableStyle(
    editor,
    (style) => {
      const next = { ...style, colWidths: evenColPct(cols) };
      delete next.autofit;
      return next;
    },
    true
  );
}

/** Word's "AutoFit contents", asked for EXPLICITLY: every column sizes to
 *  what is in it and the .docx carries `w:tblLayout w:type="autofit"` with no
 *  per-cell widths. A table that merely HAS no widths (legacy, imported) is
 *  not autofit and keeps the even fixed columns it has always printed. */
export function autofitColumns(editor: Editor): boolean {
  return patchTableStyle(
    editor,
    (style) => {
      const next = { ...style, autofit: true };
      delete next.colWidths;
      return next;
    },
    true
  );
}

/** Padding inside every cell, in points, all four sides. null = back to
 *  Word's own default margins (and the sheet's own token padding on screen). */
export function setCellPadding(editor: Editor, pt: number | null): boolean {
  return patchTableStyle(editor, (style) => {
    const next = { ...style };
    if (pt === null || !Number.isFinite(pt)) delete next.cellPadding;
    else next.cellPadding = Math.min(Math.max(pt, 0), MAX_CELL_PADDING_PT);
    return next;
  });
}

// ─── Rows ───

/** A MINIMUM height, in points, for every row the selection touches — the
 *  row still grows past it when its content needs to (Word's `atLeast`).
 *  null puts the row back to sizing itself. One transaction, one Ctrl+Z. */
export function setRowMinHeight(editor: Editor, pt: number | null): boolean {
  const { state } = editor;
  if (!isInTable(state)) return false;
  const rect = selectedRect(state);
  const value = pt === null || !Number.isFinite(pt) || pt <= 0 ? null : Math.round(pt);
  let tr: Transaction | null = null;
  let index = 0;
  rect.table.forEach((_row, offset) => {
    if (index >= rect.top && index < rect.bottom)
      tr = (tr || state.tr).setNodeAttribute(rect.tableStart + offset, 'minHeight', value);
    index++;
  });
  if (!tr) return false;
  editor.view.dispatch(tr);
  return true;
}

// ─── Cells ───

/** Where content sits in every selected cell's box. null = top, the default. */
export function setCellVAlign(editor: Editor, valign: TableCellData['valign'] | null): boolean {
  return editor
    .chain()
    .focus()
    .setCellAttribute('valign', valign ?? null)
    .run();
}

/** The minimum height of the FIRST row the selection touches, in points —
 *  what a menu shows as "current". null = the row sizes itself. */
export function selectedRowMinHeight(state: EditorState): number | null {
  if (!isInTable(state)) return null;
  const rect = selectedRect(state);
  const row = rect.table.child(Math.min(rect.top, rect.table.childCount - 1));
  const h = row?.attrs.minHeight as number | null | undefined;
  return typeof h === 'number' && h > 0 ? h : null;
}

/** The vertical alignment of the first cell in the selection — "current". */
export function selectedCellVAlign(state: EditorState): TableCellData['valign'] | null {
  const cells = selectedCells(state);
  if (!cells.length) return null;
  return (cells[0].attrs.valign as TableCellData['valign'] | null) || null;
}

/** The (horizontal) text alignment of the first cell in the selection —
 *  "current", the same shape as `selectedCellVAlign` above. Absent = left,
 *  the framework's own default. */
export function selectedCellAlign(state: EditorState): TableCellData['align'] | null {
  const cells = selectedCells(state);
  if (!cells.length) return null;
  return (cells[0].attrs.align as TableCellData['align'] | null) || null;
}

// ─── Split one table into two ───

/** Break the table in two at the row the selection is in: the rows above stay
 *  put, the selected row starts a NEW table block carrying the same style and
 *  a fresh block identity. The header row is deliberately NOT copied down —
 *  the second table is its own table, and the author decides whether it wants
 *  a header of its own. Refuses on the first row, where there would be
 *  nothing above to split off. One transaction, so one Ctrl+Z. */
export function splitTableAtSelection(editor: Editor): boolean {
  const { state } = editor;
  if (!isInTable(state)) return false;
  const rect = selectedRect(state);
  const at = rect.top;
  const table = rect.table;
  if (at <= 0 || at >= table.childCount) return false;
  const tablePos = rect.tableStart - 1;
  const rows: PMNode[] = [];
  table.forEach((row) => rows.push(row));
  const first = table.type.create(table.attrs, Fragment.fromArray(rows.slice(0, at)), table.marks);
  const second = table.type.create(
    { ...table.attrs, blockId: newBlockId() },
    Fragment.fromArray(rows.slice(at)),
    table.marks
  );
  const tr = state.tr.replaceWith(tablePos, tablePos + table.nodeSize, [first, second]);
  // Caret into the first cell of the NEW table — where the split leaves the
  // user looking.
  const $into = tr.doc.resolve(Math.min(tablePos + first.nodeSize + 3, tr.doc.content.size));
  tr.setSelection(TextSelection.near($into));
  editor.view.dispatch(tr.scrollIntoView());
  return true;
}
